import React, { useEffect, useMemo, useState } from "react";
import { Users, Gem, Ban, VolumeX, Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Community — snapshot of the streamer's audience. Read-only for now:
 * moderators/VIPs/bulk actions live in the Moderation section.
 */
export default function Community() {
  const { user } = useAuth();
  const [tab, setTab] = useState("subscribers");
  const [subs, setSubs] = useState([]);
  const [bans, setBans] = useState([]);
  const [mutes, setMutes] = useState([]);
  const [followerCount, setFollowerCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.allSettled([
      api.get(`/channels/${user.username}/subscribers`).then((r) => setSubs(r.data?.subscribers || [])),
      api.get(`/channels/${user.username}/bans`).then((r) => setBans(r.data || [])),
      api.get(`/users/mutes`).then((r) => setMutes(r.data?.mutes || r.data || [])),
      api.get(`/users/${user.username}/followers/count`).then((r) => setFollowerCount(r.data?.count ?? 0)),
    ]).finally(() => setLoading(false));
  }, [user?.username]);

  const filter = (list, field = "username") => {
    const n = q.trim().toLowerCase();
    if (!n) return list;
    return list.filter((r) => String(r[field] || r.subscriber_username || r.user_id || "").toLowerCase().includes(n));
  };

  const tabs = [
    { key: "subscribers", label: "Subscribers", count: subs.length,   icon: Gem,     color: "pink" },
    { key: "followers",   label: "Followers",   count: followerCount, icon: Users,   color: "cyan" },
    { key: "banned",      label: "Banned",      count: bans.length,   icon: Ban,     color: "orange" },
    { key: "muted",       label: "Muted",       count: mutes.length,  icon: VolumeX, color: "volt" },
  ];

  return (
    <div className="space-y-4" data-testid="section-community">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="community-tabs">
        {tabs.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 rounded flex items-center gap-2 transition text-xs sc-mono ${active ? "bg-white/5 border" : "border border-transparent hover:bg-white/[0.03]"}`}
              style={active ? { borderColor: `var(--sc-${t.color})` } : undefined}
              data-testid={`community-tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" style={{ color: active ? `var(--sc-${t.color})` : "rgb(113 113 122)" }} />
              <span className={active ? "text-zinc-100 font-bold" : "text-zinc-400"}>{t.label}</span>
              <span className="text-zinc-500 text-[10px]">{t.count}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by username…"
          className="w-full sm:max-w-sm pl-9 pr-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600"
          data-testid="community-search"
        />
      </div>

      {/* Panels */}
      {loading ? (
        <div className="term-card p-10 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-500" /></div>
      ) : tab === "subscribers" ? (
        <UserTable
          rows={filter(subs, "subscriber_username").map((s) => ({
            key: s.subscriber_username || s.user_id,
            username: s.subscriber_username || s.username || "unknown",
            sub: s.started_at ? `since ${new Date(s.started_at).toLocaleDateString()}` : "",
            badge: s.source === "stripe" ? "Stripe" : "GC",
            badgeColor: "pink",
          }))}
          empty="No active subscribers yet. Set a sub price and invite your community."
          testid="community-list-subscribers"
        />
      ) : tab === "followers" ? (
        <div className="term-card p-6 text-center" data-testid="community-followers-total">
          <Users className="w-8 h-8 mx-auto mb-3 text-[color:var(--sc-cyan)]" />
          <div className="sc-heading text-4xl font-black">{followerCount.toLocaleString()}</div>
          <div className="text-xs text-zinc-500 mt-1">All-time followers</div>
          <div className="text-[10px] text-zinc-600 mt-4">Detailed follower list is on the Phase 2 roadmap.</div>
        </div>
      ) : tab === "banned" ? (
        <UserTable
          rows={filter(bans, "username").map((b) => ({
            key: b.username, username: b.username || b.user_id,
            sub: b.reason ? `${b.reason}` : "",
            badge: "Banned", badgeColor: "orange",
          }))}
          empty="Nobody is banned from your channel."
          testid="community-list-banned"
        />
      ) : (
        <UserTable
          rows={filter(mutes, "username").map((m) => ({
            key: m.username, username: m.username || m.user_id,
            sub: "",
            badge: "Muted", badgeColor: "volt",
          }))}
          empty="You haven't muted anyone."
          testid="community-list-muted"
        />
      )}
    </div>
  );
}

function UserTable({ rows, empty, testid }) {
  return (
    <div className="term-card p-0 overflow-hidden" data-testid={testid}>
      {rows.length === 0 ? (
        <div className="p-10 text-center text-xs text-zinc-500">{empty}</div>
      ) : (
        <ul className="divide-y divide-white/5">
          {rows.map((r) => (
            <li key={r.key} className="flex items-center px-4 py-3 gap-3 hover:bg-white/[0.02]" data-testid={`community-user-${r.username}`}>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-zinc-800 to-zinc-900 grid place-items-center text-xs font-black text-zinc-500">
                {r.username[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="sc-heading text-sm font-bold truncate">{r.username}</div>
                {r.sub && <div className="text-[10px] sc-mono text-zinc-500">{r.sub}</div>}
              </div>
              <span
                className="text-[9px] sc-mono px-1.5 py-0.5 rounded font-bold"
                style={{ background: `var(--sc-${r.badgeColor})22`, color: `var(--sc-${r.badgeColor})` }}
              >
                {r.badge}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
